import React, {useMemo, useState} from "react";
import {Alert, Badge, Button, Form, Modal, ProgressBar, Spinner, Table} from "react-bootstrap";
import {toast} from "react-toastify";
import {adminReconcileNegativeBalances} from "../../http/bookAPI";

const fmtVirt = (value) => `${Number(value || 0).toFixed(0)}V`;
const fmtUsd = (value) => `$${Number(value || 0).toFixed(2)}`;

const actionVariant = (action) => {
    if (action === "canceled_assignments") return "danger";
    if (action === "books_needfix") return "warning";
    if (action === "would_cancel_assignments") return "warning";
    if (action === "would_needfix_books") return "warning";
    return "secondary";
};

const actionLabel = (action) => {
    if (action === "canceled_assignments") return "canceled";
    if (action === "books_needfix") return "needfix";
    if (action === "would_cancel_assignments") return "would cancel";
    if (action === "would_needfix_books") return "would needfix";
    if (action === "no_new_assignments") return "nothing";
    return action || "-";
};

const compactIds = (ids = [], max = 6) => {
    if (!Array.isArray(ids) || !ids.length) return "-";
    const head = ids.slice(0, max).join(", ");
    const rest = ids.length - max;
    return rest > 0 ? `${head}, +${rest}` : head;
};

const AdminNegativeBalanceReconcile = ({onApplied}) => {
    const [show, setShow] = useState(false);
    const [dryRun, setDryRun] = useState(true);
    const [limitUsers, setLimitUsers] = useState(50);
    const [maxCancelsPerUser, setMaxCancelsPerUser] = useState(50);
    const [markBooksNeedFix, setMarkBooksNeedFix] = useState(true);
    const [excludeStillNegative, setExcludeStillNegative] = useState(false);
    const [loading, setLoading] = useState(false);
    const [rows, setRows] = useState([]);
    const [totals, setTotals] = useState({
        users: 0,
        canceledAssignments: 0,
        refundVirt: 0,
        refundUsd: 0,
        booksNeedFix: 0,
        stillNegative: 0,
    });
    const [lastMode, setLastMode] = useState(null);

    const summary = useMemo(() => totals || {}, [totals]);
    const percent = loading ? 45 : (rows.length || summary.checkedUsers ? 100 : 0);
    const progressLabel = loading
        ? "Running..."
        : (excludeStillNegative && Number(summary.checkedUsers || 0) > Number(summary.users || 0)
            ? `${summary.users || 0} eligible / ${summary.checkedUsers || 0} checked`
            : `${summary.users || 0} users`);

    const resetState = () => {
        setRows([]);
        setTotals({
            users: 0,
            canceledAssignments: 0,
            refundVirt: 0,
            refundUsd: 0,
            booksNeedFix: 0,
            stillNegative: 0,
        });
        setLastMode(null);
    };

    const handleClose = () => {
        if (loading) return;
        resetState();
        setShow(false);
    };

    const runAudit = async () => {
        const safeLimitUsers = Math.max(1, Number(limitUsers) || 50);
        const safeMaxCancels = Math.max(1, Number(maxCancelsPerUser) || 50);
        setLoading(true);
        try {
            const result = await adminReconcileNegativeBalances({
                dryRun,
                limitUsers: safeLimitUsers,
                maxCancelsPerUser: safeMaxCancels,
                markBooksNeedFix,
                excludeStillNegative,
            });
            setRows(Array.isArray(result.rows) ? result.rows : []);
            setTotals(result.totals || {});
            setLastMode(result.applied ? "apply" : "dry");
            if (result.applied && typeof onApplied === "function") onApplied();
            if (result.applied) {
                toast.success(`Reconciled ${result.totals?.users || 0} negative-balance users.`);
            } else if (!result.rows?.length) {
                toast.info("No users with negative Virt balance found.");
            }
        } catch (e) {
            toast.error(e?.response?.data?.message || e?.message || "Failed to reconcile negative balances.");
        } finally {
            setLoading(false);
        }
    };

    return (
        <>
            <Button variant="outline-danger" size="sm" onClick={() => setShow(true)}>
                Debt Audit
            </Button>

            <Modal
                show={show}
                onHide={handleClose}
                size="xl"
                top
                dialogClassName="negative-balance-audit-modal"
            >
                <Modal.Header closeButton={!loading} className="py-2 px-3">
                    <Modal.Title className="h5 mb-0">Negative Balance Reconcile</Modal.Title>
                </Modal.Header>
                <Modal.Body className="py-3 px-3">
                    <Alert variant="light" className="border small py-2 px-3 mb-3">
                        Finds users with negative Virt balances. Dry run previews actions; apply mode cancels their open NEW assignments, returns funds to the owner balance, and moves their books/products to NEEDFIX if the debt still remains.
                    </Alert>

                    <div className="d-flex flex-wrap align-items-end gap-2 mb-2 small">
                        <Form.Check
                            type="switch"
                            id="negative-balance-dry-run"
                            label="Dry run"
                            checked={dryRun}
                            disabled={loading}
                            onChange={(e) => setDryRun(e.target.checked)}
                        />
                        <Form.Group style={{width: 120}}>
                            <Form.Label className="small mb-1">User limit</Form.Label>
                            <Form.Control
                                size="sm"
                                type="number"
                                min={1}
                                max={500}
                                value={limitUsers}
                                disabled={loading}
                                onChange={(e) => setLimitUsers(Number(e.target.value) || 50)}
                            />
                        </Form.Group>
                        <Form.Group style={{width: 150}}>
                            <Form.Label className="small mb-1">Max cancels/user</Form.Label>
                            <Form.Control
                                size="sm"
                                type="number"
                                min={1}
                                max={500}
                                value={maxCancelsPerUser}
                                disabled={loading}
                                onChange={(e) => setMaxCancelsPerUser(Number(e.target.value) || 50)}
                            />
                        </Form.Group>
                        <Form.Check
                            type="switch"
                            id="negative-balance-needfix"
                            label="Needfix books"
                            checked={markBooksNeedFix}
                            disabled={loading}
                            onChange={(e) => setMarkBooksNeedFix(e.target.checked)}
                        />
                        <Form.Check
                            type="switch"
                            id="negative-balance-skip-still-negative"
                            label="Skip still negative"
                            checked={excludeStillNegative}
                            disabled={loading}
                            onChange={(e) => setExcludeStillNegative(e.target.checked)}
                        />
                        <Button variant={dryRun ? "success" : "danger"} size="sm" disabled={loading} onClick={runAudit}>
                            {loading ? <Spinner size="sm" /> : dryRun ? "Start Dry Run" : "Start and Apply"}
                        </Button>
                    </div>

                    <ProgressBar now={percent} label={progressLabel} className="mb-2" style={{height: 18}} />

                    <div className="d-flex flex-wrap gap-2 mb-2 small">
                        <Badge bg={lastMode === "apply" ? "danger" : "warning"} text={lastMode === "apply" ? undefined : "dark"}>
                            {lastMode === "apply" ? "Processed" : "Candidates"}: {summary.users || 0}
                        </Badge>
                        <Badge bg="secondary">Checked: {summary.checkedUsers ?? summary.users ?? 0}</Badge>
                        <Badge bg={Number(summary.skippedStillNegative || 0) > 0 ? "dark" : "secondary"}>Skipped: {summary.skippedStillNegative || 0}</Badge>
                        <Badge bg="secondary">Canceled: {summary.canceledAssignments || 0}</Badge>
                        <Badge bg="secondary">Refund Virt: {fmtVirt(summary.refundVirt)}</Badge>
                        <Badge bg="secondary">Refund USD: {fmtUsd(summary.refundUsd)}</Badge>
                        <Badge bg="secondary">Needfix books: {summary.booksNeedFix || 0}</Badge>
                        <Badge bg={(summary.stillNegative || 0) > 0 ? "danger" : "success"}>Still negative: {summary.stillNegative || 0}</Badge>
                    </div>

                    <div className="negative-balance-audit-table-wrap">
                        <Table striped bordered hover size="sm" className="small align-middle mb-0 negative-balance-audit-table">
                            <thead>
                            <tr>
                                <th>UID</th>
                                <th>User</th>
                                <th>Before</th>
                                <th>After</th>
                                <th>Cancel</th>
                                <th>Refund</th>
                                <th>Needfix</th>
                                <th>Action</th>
                                <th>Note</th>
                            </tr>
                            </thead>
                            <tbody>
                            {rows.length ? rows.map((row) => (
                                <tr key={row.userId}>
                                    <td>{row.userId}</td>
                                    <td>{row.username || row.email || "-"}</td>
                                    <td>{fmtVirt(row.balanceBefore)}</td>
                                    <td className={Number(row.balanceAfter || 0) < 0 ? "text-danger fw-semibold" : "text-success fw-semibold"}>{fmtVirt(row.balanceAfter)}</td>
                                    <td>{row.canceledCount || 0}<br /><span className="text-muted">{compactIds(row.canceledAssignmentIds)}</span></td>
                                    <td>{fmtVirt(row.refundVirt)} / {fmtUsd(row.refundUsd)}</td>
                                    <td>{row.booksNeedFixCount || 0}<br /><span className="text-muted">{compactIds(row.booksNeedFixIds)}</span></td>
                                    <td><Badge bg={actionVariant(row.action)}>{actionLabel(row.action)}</Badge></td>
                                    <td>{row.note || "-"}</td>
                                </tr>
                            )) : (
                                <tr>
                                    <td colSpan={9} className="text-center text-muted">Run audit to load users with negative Virt balance.</td>
                                </tr>
                            )}
                            </tbody>
                        </Table>
                    </div>
                </Modal.Body>
                <Modal.Footer className="py-2 px-3">
                    <Button variant="secondary" className="btn-sm" disabled={loading} onClick={handleClose}>Close</Button>
                </Modal.Footer>
            </Modal>
            <style>{`
                .negative-balance-audit-modal {
                    max-width: min(1540px, calc(100vw - 96px));
                    width: min(1540px, calc(100vw - 96px));
                }
                .negative-balance-audit-table-wrap {
                    max-height: 62vh;
                    overflow: auto;
                }
                .negative-balance-audit-table {
                    font-size: 0.74rem;
                    table-layout: fixed;
                }
                .negative-balance-audit-table th,
                .negative-balance-audit-table td {
                    padding: 0.16rem 0.26rem;
                    line-height: 1.08;
                    vertical-align: middle;
                    word-break: break-word;
                }
                .negative-balance-audit-table .badge {
                    font-size: 0.63rem;
                    padding: 0.18rem 0.34rem;
                    white-space: nowrap;
                }
                .negative-balance-audit-table th:nth-child(1) { width: 54px; }
                .negative-balance-audit-table th:nth-child(2) { width: 180px; }
                .negative-balance-audit-table th:nth-child(3) { width: 86px; }
                .negative-balance-audit-table th:nth-child(4) { width: 86px; }
                .negative-balance-audit-table th:nth-child(5) { width: 108px; }
                .negative-balance-audit-table th:nth-child(6) { width: 116px; }
                .negative-balance-audit-table th:nth-child(7) { width: 112px; }
                .negative-balance-audit-table th:nth-child(8) { width: 132px; }
            `}</style>
        </>
    );
};

export default AdminNegativeBalanceReconcile;
